import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import Header from "@components/ui/Header";
import Icon from "@components/AppIcon";
import Button from "@components/ui/Button";
import LocationCard from "./components/LocationCard";
import InteractiveMap from "./components/InteractiveMap";
import LocationDetails from "./components/LocationDetails";
import VirtualTour from "./components/VirtualTour";
import LocationFilter from "./components/LocationFilter";
import Footer from "@components/ui/Footer";

const LocationsPage = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [showVirtualTour, setShowVirtualTour] = useState(false);
  const [filters, setFilters] = useState({
    service: "",
    area: "",
    feature: "",
  });
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(Date.now());

  // Fetch locations from API with useCallback to prevent infinite loops
  const fetchLocations = useCallback(async () => {
    try {
      const response = await fetch(`/api/our_branches?t=${Date.now()}`);
      if (!response.ok) {
        throw new Error("Failed to fetch locations");
      }
      const data = await response.json();
      setLocations(data.data || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  // Refresh data function
  const refreshData = () => {
    setLastUpdated(Date.now());
  };

  // Fetch locations on component mount and when lastUpdated changes
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations, lastUpdated]);

  // Filter locations based on current filters
  useEffect(() => {
    let filtered = locations;

    if (filters.service) {
      filtered = filtered.filter(
        (location) =>
          location.specializations.some((spec) =>
            spec.toLowerCase().includes(filters.service.toLowerCase())
          ) ||
          location.services.some((service) =>
            service.name.toLowerCase().includes(filters.service.toLowerCase())
          )
      );
    }

    if (filters.area) {
      filtered = filtered.filter((location) => location.area === filters.area);
    }

    if (filters.feature) {
      filtered = filtered.filter((location) => {
        switch (filters.feature) {
          case "emergency":
            return location.emergencyAvailable;
          case "parking":
            return location.features.some((f) =>
              f.toLowerCase().includes("parking")
            );
          case "wheelchair":
            return location.features.some((f) =>
              f.toLowerCase().includes("wheelchair")
            );
          case "pharmacy":
            return location.features.some((f) =>
              f.toLowerCase().includes("pharmacy")
            );
          case "surgery":
            return location.features.some((f) =>
              f.toLowerCase().includes("surgery")
            );
          default:
            return true;
        }
      });
    }

    setFilteredLocations(filtered);
  }, [filters, locations]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      service: "",
      area: "",
      feature: "",
    });
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleViewDetails = (location) => {
    setSelectedLocation(location);
    setShowLocationDetails(true);
  };

  const handleVirtualTour = (location) => {
    setSelectedLocation(location);
    setShowVirtualTour(true);
  };

  // Refresh data when modals close to ensure we have the latest data
  const handleModalClose = () => {
    setShowLocationDetails(false);
    setShowVirtualTour(false);
    refreshData();
  };

  return (
    <div className="min-h-screen bg-background-canvas">
      <Helmet>
        <title>
          Our Branches - Eye&U Kenya | Accessible Eye Care Across Kenya
        </title>
        <meta
          name="description"
          content="Discover Eye&U Kenya branches near you. We bring world-class eye care closer with modern clinics in Nairobi and across Kenya."
        />
        <meta
          name="keywords"
          content="eye clinic Kenya, eye hospital Nairobi, Eye&U branches, eye specialists Kenya, eye care locations, ophthalmology clinics"
        />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section
        className="relative pt-28 pb-20 md:pt-32 md:pb-28 min-h-[480px] flex items-center"
        style={{
          backgroundImage: `url('/storage/gallery_images/slider-3.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-blue-900/70" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6 drop-shadow-lg">
            Find Your Nearest Eye&U Clinic
          </h1>

          <p className="text-lg md:text-xl lg:text-2xl text-white/95 mb-10 leading-relaxed drop-shadow-md max-w-3xl mx-auto">
            {locations.length} convenient locations across Kenya, each designed
            as a community health hub with specialized services and local
            connections.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-white/90">
            <div className="flex items-center gap-2 text-lg font-medium">
              <Icon name="MapPin" size={22} className="text-[#eb2227]" />
              <span>{locations.length} Locations</span>
            </div>
            <div className="flex items-center gap-2 text-lg font-medium">
              <Icon name="Clock" size={22} className="text-[#eb2227]" />
              <span>Extended Hours</span>
            </div>
            <div className="flex items-center gap-2 text-lg font-medium">
              <Icon name="AlertCircle" size={22} className="text-[#eb2227]" />
              <span>Emergency Care</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
        <LocationFilter
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          totalLocations={locations.length}
          filteredCount={filteredLocations.length}
        />

        <div className="mb-12">
          <InteractiveMap
            locations={filteredLocations}
            selectedLocation={selectedLocation}
            onLocationSelect={handleLocationSelect}
          />
        </div>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading font-bold text-2xl text-text-primary">
              Our Branches
            </h2>
            <div className="flex items-center space-x-4">
              <div className="text-text-secondary">
                {filteredLocations.length} location
                {filteredLocations.length !== 1 ? "s" : ""} found
              </div>
              <Button variant="outline" size="sm" onClick={refreshData}>
                <Icon name="RefreshCw" size={14} className="mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {filteredLocations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredLocations.map((location) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  onViewDetails={handleViewDetails}
                  onVirtualTour={handleVirtualTour}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon
                name="MapPin"
                size={48}
                className="text-text-secondary mx-auto mb-4"
              />
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                No locations found
              </h3>
              <p className="text-text-secondary mb-6">
                Try adjusting your filters to see more locations
              </p>
              <Button
                variant="outline"
                onClick={handleClearFilters}
                iconName="RotateCcw"
                iconPosition="left"
                iconSize={16}
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      {showLocationDetails && selectedLocation && (
        <LocationDetails location={selectedLocation} onClose={handleModalClose} />
      )}

      {showVirtualTour && selectedLocation && (
        <VirtualTour location={selectedLocation} onClose={handleModalClose} />
      )}

      <Footer />
    </div>
  );
};

export default LocationsPage;
